(ns ui.editors.card.view
  (:require [clojure.string :as string]
            [common.localization :as loc]
            [common.util :as cu]
            [re-frame.core :as rf]
            [ui.components.button.view :as button]
            [ui.components.input.simple-text :as simple-text-input]
            [ui.components.input.textarea :as textarea]
            [ui.components.select.select :as select]
            [ui.components.tab-strip.view :as tab-strip]
            [ui.editors.card.associations.view :as associations-card-editor]
            [ui.editors.card.entity-list.view :as entity-list-card-editor]
            [ui.editors.card.event :as event]
            [ui.editors.card.fees.view :as fees-card-editor]
            [ui.editors.card.model :as model]
            [ui.editors.card.property-list.sub :as property-list-sub]
            [ui.editors.card.property-list.view :as property-list-card-editor]
            [ui.editors.card.sub :as sub]
            [ui.util :as uu]
            [ui.view.card.view :as card-view]))


(defn full-screen-modal
  [& children]
  [:div
   {:class "fixed top-0 left-0 flex flex-col z-[102] w-screen max-w-full h-screen max-h-screen overflow-hidden bg-base-light"}
   (into [:<>] children)])

(defn card-type-specific-editor
  []
  (let [card-type @(rf/subscribe [::sub/card-type])]
    (case card-type
      :card.type/property-list
      [property-list-card-editor/view]
      :card.type/associations
      [associations-card-editor/view]
      :card.type/fees
      [fees-card-editor/view]

      ;; TODO: Entity list is currently an umbrella editor for cards that have entity lists.
      ;; Maybe later we'll later consolidate all entity listing cards (tasks, fees, etc) into a single card type "entity list"
      :card.type/tasks
      [entity-list-card-editor/view]
      :card.type/risks
      [entity-list-card-editor/view]
      :card.type/value-creations
      [entity-list-card-editor/view]

      (str "No editor yet for card type " card-type))))

(defn save-card-button
  []
  (let [{:keys [disabled? tooltip-label]} @(rf/subscribe [::sub/submit-button-state])]
    [button/sm-primary-solid
     {:on-click  #(rf/dispatch [::event/close-editor
                                {:apply-changes? true}])
      :disabled? disabled?
      :tooltip   tooltip-label}
     (loc/tr :card-editor/apply-changes)]))

(defn tab-selector
  []
  (let [current-tab @(rf/subscribe [::sub/current-tab])
        card-type @(rf/subscribe [::sub/card-type])
        preview-disabled? (case card-type
                            :card.type/property-list @(rf/subscribe [::property-list-sub/preview-disabled?])
                            false)
        preview-disabled-tooltip (case card-type
                                   :card.type/property-list
                                   (loc/tr :card-editor/add-at-least-one-field-to-card-first)
                                   nil)]
    [:div
     {:class "flex justify-center items-center"}
     [tab-strip/view
      {:tabs         [{:value :editor
                       :label [:span
                               {:class "flex gap-2"}
                               [:i {:class "fal fa-pencil text-sm m-0"}] (loc/tr :card-editor/edit-card)]}
                      ;; TODO: add tooltip if needed
                      {:value     :preview
                       :disabled? preview-disabled?
                       :label     [:span
                                   {:class "flex gap-2"}
                                   [:i {:class "fal fa-eye text-sm m-0"}] (loc/tr :card-editor/preview-card)]}]
       :selected-tab current-tab
       :on-change!   (fn [value-str]
                       (rf/dispatch-sync [::event/set-current-tab (keyword value-str)]))}]]))

(defn common-fields-editor
  []
  (let [card-title @(rf/subscribe [::sub/card-title])
        card-description @(rf/subscribe [::sub/card-description])
        set-card-title (fn [title]
                         (rf/dispatch-sync [::event/set-card-title title]))
        set-card-description (fn [desc]
                               (rf/dispatch-sync [::event/set-card-description desc]))]
    [:div
     {:class "flex flex-col gap-5 items-stretch"}
     [:div
      {:class "flex flex-col lg:flex-row items-start gap-3 lg:gap-5 "}
      [:div
       {:class "w-1/3"}
       [:p {:class "font-medium text-base-light-text font-muli"}
        (loc/tr :card-editor/card-title-label)]
       [:p
        {:class "text-sm m-0 text-base-light-text-paler"}
        (loc/tr :card-editor/card-title-helper)]]

      [:div
       {:class "w-2/3"}
       [simple-text-input/view
        {:full-width? true
         :value       card-title
         :on-change   set-card-title}]]]

     [:div
      {:class "flex flex-col lg:flex-row items-start gap-3 lg:gap-5 "}
      [:div
       {:class "w-full  lg:w-1/3"}
       [:p
        {:class "font-medium text-base-light-text"}
        (loc/tr :card-editor/card-description-label)]
       [:p
        {:class "text-base-light-text-paler text-sm m-0"}
        (loc/tr :card-editor/card-description-helper)]]
      [:div
       {:class "w-full lg:w-2/3"}
       [textarea/view
        {:class     "w-full"
         :value     card-description
         :on-change set-card-description}]]]]))

(defn header-right
  []
  [:div
   {:class "flex gap-2 items-center gap-3"}
   [save-card-button]
   [button/sm-secondary-hollow
    {:on-click #(rf/dispatch [::event/close-editor
                              {:apply-changes? false}])}
    (loc/tr :cancel)]])

(defn header-left
  []
  [:div
   {:class "flex items-center gap-2"}
   [:span {:class "text-lg font-medium"} "Configure card"]])

(defn preview
  []
  (let [card @(rf/subscribe [::sub/card])
        entity-type @(rf/subscribe [::sub/entity-type])
        preview-entity-id @(rf/subscribe [::sub/preview-entity-id])
        preview-entity-options @(rf/subscribe [::sub/preview-entity-options])
        preview-card-default-width @(rf/subscribe [::sub/preview-card-default-width])]
    [:div
     {:class "flex flex-col items-center"}
     [:div
      {:class (uu/cm "flex flex-col gap-10 max-w-screen")
       :style {:width (if preview-card-default-width
                        (str preview-card-default-width "px")
                        "800px")}}
      [:div
       {:class "flex flex-col gap-1"}

       [:label
        {:class "font-medium text-base-light-text"}
        (loc/tr entity-type)]
       [select/view
        {:value     (->> preview-entity-options
                         (cu/filter-by :value preview-entity-id)
                         (first))
         :options   preview-entity-options
         :on-change (fn [entity-id]
                      (rf/dispatch [::event/select-preview-entity entity-id]))}]
       [:p
        {:class "text-sm m-0 text-base-light-text-paler"}
        (loc/tr :card-editor/preview-entity-select-helper [(loc/tr entity-type)])]]
      (if preview-entity-id
        ^{:key (str "preview-card-" (:db/id card) "-" preview-entity-id)}
        [card-view/view-with-data-fetching
         {:card            card
          :entity-id       preview-entity-id
          :entity-type     entity-type
          :preview-mode?   true
          :card-state-path model/preview-card-state-full-path}]
        [:div
         {:class "flex flex-col items-center justify-center rounded shadow-lg h-80 border"}
         [:p (loc/tr :select-entity-type-first [(-> entity-type loc/tr string/lower-case)])]])]]))

(defn editor
  []
  [:div
   {:class "mx-auto flex flex-col gap-5 xl:max-w-10/12
            divide-y divide-solid divide-base-light-border-paler"}
   [common-fields-editor]
   [card-type-specific-editor]])

(defn content
  []
  (let [current-tab @(rf/subscribe [::sub/current-tab])]
    [:div
     {:class "p-5 grow flex flex-col gap-10 oveflow-hidden overflow-y-auto
              bg-base-light"}
     (case current-tab
       :editor [editor]
       :preview [preview]
       (str "Unknown editor tab: " current-tab))]))

(defn header
  []
  [:div
   {:class "grid grid-cols-4 px-4 py-3 border-b border-solid border-base-light-border-paler"}
   [:div
    {:class "flex justify-start"}
    [header-left]]
   [:div
    {:class "col-span-2 flex justify-center"}
    [tab-selector]]
   [:div
    {:class "flex justify-end items-center gap-3"}
    [header-right]]])

(defn view
  []
  (let [initialized? @(rf/subscribe [::sub/initialized?])]
    (when initialized?
      [:div
       {:class "flex flex-col max-h-full overflow-hidden"}
       [header]
       [content]])))

(defn modal-view
  []
  (let [show-modal? @(rf/subscribe [::sub/show-modal?])]
    (when show-modal?
      [full-screen-modal
       [view]])))
